/**
 * TodoList — shared session-todo item list.
 *
 * One render of the agent's task list, reused by the persistent TodoPanel
 * (dock above the input) and the `todowrite` tool row's expanded detail.
 * Status icon + content (struck through when done) + priority dot.
 */
import { Circle, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import type { TodoItem } from './hooks.js'

export const TODO_PRIORITY_DOTS: Record<TodoItem['priority'], string> = {
  high: 'bg-red-400',
  medium: 'bg-yellow-400',
  low: 'bg-zinc-500',
}

/** in_progress spins in the LIVE dock but is static in a historical tool
 *  row (a persisted call shouldn't show a forever-spinning loader). */
function statusIcon(status: TodoItem['status'], animate: boolean): React.ReactNode {
  switch (status) {
    case 'pending': return <Circle size={12} className="text-zinc-400" />
    case 'in_progress': return <Loader2 size={12} className={`text-blue-400 ${animate ? 'animate-spin' : ''}`} />
    case 'completed': return <CheckCircle2 size={12} className="text-green-400" />
    case 'cancelled': return <XCircle size={12} className="text-zinc-500" />
  }
}

interface Props {
  todos: TodoItem[]
  /** Animate the in_progress spinner (dock = true, historical row = false). */
  animateInProgress?: boolean
  showPriority?: boolean
}

export function TodoList({ todos, animateInProgress = true, showPriority = true }: Props) {
  return (
    <div className="space-y-1">
      {todos.map(todo => (
        <div key={todo.id} data-status={todo.status} className="flex items-start gap-2 text-xs">
          <div className="shrink-0 mt-0.5">{statusIcon(todo.status, animateInProgress)}</div>
          <span className={`flex-1 break-words ${
            todo.status === 'completed' || todo.status === 'cancelled'
              ? 'themed-text-muted line-through'
              : 'themed-text'
          }`}>
            {todo.content}
          </span>
          {showPriority && (
            <div
              className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${TODO_PRIORITY_DOTS[todo.priority]}`}
              title={`${todo.priority} priority`}
            />
          )}
        </div>
      ))}
    </div>
  )
}
